import { ModalPresence } from '../components/ModalPresence';
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Clock, Copy, ExternalLink, Landmark, Link2, Wallet, X } from 'lucide-react';
import { api, formatMoney } from '../api';

interface Creator {
  slug: string;
  display_name: string;
  url: string;
}

interface AffiliateApplication {
  id: string;
  display_name: string;
  requested_slug: string;
  platform_url: string;
  promotion_plan: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  can_reapply_at: string | null;
}

interface AffiliateProgram {
  status: 'active' | 'suspended';
  terms: {
    discount_percent: number;
    discount_duration_days: number | null;
    commission_percent: number;
    commission_duration_days: number | null;
    approved_at: string;
    lifetime_plan: string | null;
  };
  slug: string;
  url: string;
  aliases: Array<{ slug: string; is_current: boolean }>;
  payout: {
    method: 'paypal' | 'bank' | null;
    country: string | null;
    full_name: string | null;
    paypal_email: string | null;
    bank_iban: string | null;
    bank_swift: string | null;
  };
  balance: {
    available_cents: number;
    reserved_cents: number;
    paid_cents: number;
    earned_cents: number;
    reversed_cents: number;
    currency: string;
  };
  referred: { referred: number; paying: number };
  commissions: Array<{
    id: string;
    kind: 'invoice' | 'reversal' | 'restore';
    status: string;
    currency: string;
    invoice_exclusive_cents: number;
    commission_cents: number;
    stripe_invoice_id: string | null;
    note: string | null;
    created_at: string;
  }>;
  payouts: Array<{
    id: string;
    method: string;
    amount_cents: number;
    currency: string;
    status: string;
    requested_at: string;
    processed_at: string | null;
    admin_note: string | null;
  }>;
}

const visitorFaqs = [
  ['Who can join?', 'Approved creators only. Apply with the form. If we accept, the program opens on this page and you get a vanity link.'],
  ['When is an account attached?', 'The first valid creator link or referral ID on this browser sticks to the account at signup, including Discord. After the first paid invoice it cannot move.'],
  ['What pays commission?', 'Our standard offer is 10% recurring commission for life, excluding tax, with 10% off the first month for referred customers. Approved terms may differ. Large organizations can discuss a custom agreement at hello@bckt.io.'],
  ['Can I refer myself?', 'No. Same account, same email, same Discord, or the same network is blocked.'],
];

const creatorFaqs = [
  ['When do I get paid?', 'Commission posts when Stripe marks the invoice paid. Chargebacks and refunds reverse it, even after 30 days. The balance can go negative.'],
  ['Why can the balance be -$8?', 'If four buyers reverse after you already accrued their commission, those rows stay on the ledger. Later invoices clear the debt before the next payout.'],
  ['What is the payout minimum?', '$5.00 available, with no open negative balance. PayPal needs a legal name and email. Bank transfer needs IBAN; SWIFT is optional.'],
  ['Does changing my link steal old referrals?', 'No. Old /r/ links stay as aliases. New signups use the current ID. Attached accounts stay attached.'],
];

function money(cents: number, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

function kindLabel(kind: AffiliateProgram['commissions'][number]['kind'], note: string | null) {
  if (kind === 'invoice') {
    if (note === 'subscription_create') return 'First paid invoice';
    if (note === 'subscription_cycle') return 'Renewal';
    if (note === 'subscription_update') return 'Upgrade';
    return 'Paid invoice';
  }
  if (kind === 'reversal') return note === 'dispute' ? 'Chargeback' : 'Refund';
  return 'Dispute won';
}

function durationLabel(days: number | null) {
  if (days === null) return 'Lifetime';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

function Faq({ items }: { items: string[][] }) {
  return (
    <dl className="affiliate-faq">
      {items.map(([question, answer]) => (
        <div key={question}>
          <dt>{question}</dt>
          <dd>{answer}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Affiliates() {
  const client = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string }>();
  const [copied, setCopied] = useState(false);
  const [selectedMethod, setMethod] = useState<'paypal' | 'bank' | null>(null);
  const [section, setSection] = useState<'overview' | 'activity' | 'settings'>('overview');
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyName, setApplyName] = useState('');
  const [applySlug, setApplySlug] = useState('');
  const [applyPlatform, setApplyPlatform] = useState('');
  const [applyPlan, setApplyPlan] = useState('');
  const [applyContact, setApplyContact] = useState('');

  const submitApplication = useMutation({
    mutationFn: (body: {
      display_name: string;
      requested_slug: string;
      platform_url: string;
      promotion_plan: string;
      contact_info: string;
    }) => api('/affiliates/apply', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['affiliates'] });
      setApplyModalOpen(false);
      setNotice({ tone: 'success', text: 'Application submitted! We will review it shortly.' });
    },
    onError: (err: any) => {
      setNotice({ tone: 'error', text: err.message || 'Could not submit application.' });
    },
  });
  const query = useQuery({
    queryKey: ['affiliates'],
    queryFn: () =>
      api<{
        apply_url: string;
        min_payout_cents: number;
        countries: Array<{ code: string; name: string }>;
        supporting: (Creator & { locked: boolean }) | null;
        program: AffiliateProgram | null;
        application: AffiliateApplication | null;
      }>('/affiliates'),
  });

  const saveSlug = useMutation({
    mutationFn: (slug: string) => api('/affiliates/slug', { method: 'POST', body: JSON.stringify({ slug }) }),
    onSuccess: () => {
      setNotice({ tone: 'success', text: 'Referral ID updated. Older links still work.' });
      void client.invalidateQueries({ queryKey: ['affiliates'] });
    },
    onError: (error: Error) => setNotice({ tone: 'error', text: error.message }),
  });
  const saveMethod = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api('/affiliates/payout-method', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setNotice({ tone: 'success', text: 'Payout details saved.' });
      void client.invalidateQueries({ queryKey: ['affiliates'] });
    },
    onError: (error: Error) => setNotice({ tone: 'error', text: error.message }),
  });
  const requestPayout = useMutation({
    mutationFn: () => api('/affiliates/payouts', { method: 'POST' }),
    onSuccess: () => {
      setNotice({ tone: 'success', text: 'Payout requested. We send it by transfer after review.' });
      void client.invalidateQueries({ queryKey: ['affiliates'] });
    },
    onError: (error: Error) => setNotice({ tone: 'error', text: error.message }),
  });

  const program = query.data?.program;
  const method = selectedMethod ?? program?.payout.method ?? 'paypal';
  const available = program?.balance.available_cents ?? 0;
  const canWithdraw = Boolean(program?.payout.method) && available >= (query.data?.min_payout_cents ?? 500);
  const openReversals = useMemo(
    () => program?.commissions.filter((row) => row.kind === 'reversal' && row.status === 'open').length ?? 0,
    [program],
  );

  const copyLink = async () => {
    if (!program) return;
    await navigator.clipboard.writeText(program.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const submitSlug = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    saveSlug.mutate(String(form.get('slug') ?? ''));
  };

  const submitPayout = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    saveMethod.mutate({
      method,
      country: String(form.get('country') ?? ''),
      full_name: String(form.get('full_name') ?? ''),
      ...(method === 'paypal'
        ? { paypal_email: String(form.get('paypal_email') ?? '') }
        : { iban: String(form.get('iban') ?? ''), swift: String(form.get('swift') ?? '') }),
    });
  };

  if (query.isLoading) return <div className="dashboard-page affiliates-page"><div className="admin-loading" /></div>;
  if (query.isError) {
    return (
      <div className="dashboard-page affiliates-page">
        <div className="dash-card empty-state">
          <h3>Affiliates could not load</h3>
          <button className="button button--dash-quiet" onClick={() => void query.refetch()}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page affiliates-page">
      <div className="dash-heading">
        <div className="dash-heading__copy">
          <p>Creators</p>
          <h1>Affiliates</h1>
        </div>
      </div>
      {notice ? <div className={`settings-notice settings-notice--${notice.tone}`}>{notice.text}</div> : null}

      {!program ? (
        <>
          <section className="affiliate-hero">
            <div>
              <p>Standard partner offer</p>
              <h2>Your community. Your referral link.</h2>
              <p>
                Our standard offer gives your community 10% off their first month and you 10% recurring
                commission for life. Your approved agreement sets the final rates and durations.
              </p>
              <p>
                Represent a large organization? Discuss custom terms with us at{' '}
                <a href="mailto:hello@bckt.io">hello@bckt.io</a>.
              </p>
            </div>
            {query.data?.application?.status === 'pending' ? (
              <div className="affiliate-app-banner affiliate-app-banner--pending">
                <Clock />
                <div>
                  <strong>Application under review</strong>
                  <span>Requested code: <code>bckt.io/r/{query.data.application.requested_slug}</code></span>
                </div>
              </div>
            ) : query.data?.application?.status === 'rejected' && query.data.application.can_reapply_at && new Date(query.data.application.can_reapply_at) > new Date() ? (
              <div className="affiliate-app-banner affiliate-app-banner--rejected">
                <AlertCircle />
                <div>
                  <strong>Application not accepted</strong>
                  <span>
                    {query.data.application.rejection_reason || 'Does not meet current creator partner requirements.'}
                  </span>
                  <small>
                    You can reapply on {new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(query.data.application.can_reapply_at))}.
                  </small>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="button button--dash-primary"
                onClick={() => { submitApplication.reset(); setApplyModalOpen(true); }}
              >
                {query.data?.application?.status === 'rejected' ? 'Reapply for partner' : 'Apply for Creator Partner'}
              </button>
            )}
          </section>
          <div className="affiliate-facts affiliate-facts--visitor">
            <article><span>Standard commission</span><strong>10%</strong><small>Lifetime on all renewals</small></article>
            <article><span>Standard customer discount</span><strong>10% off</strong><small>Discount on their 1st month</small></article>
            <article><span>Payout</span><strong>$5 min</strong><small>PayPal or IBAN</small></article>
          </div>
          {query.data?.supporting ? (
            <p className="affiliate-support-note">
              This account currently supports <strong>{query.data.supporting.display_name}</strong> via{' '}
              <code>{query.data.supporting.slug}</code>.
            </p>
          ) : null}
          <section className="people-block">
            <div className="people-block__head">
              <h2>Before you apply</h2>
              <p>Tell us about your community. Once approved, your link and agreed terms appear here.</p>
            </div>
            <Faq items={visitorFaqs} />
          </section>
        </>
      ) : (
        <>
          {program.status === 'suspended' ? (
            <div className="settings-notice settings-notice--error">This affiliate account is suspended. Existing attached buyers stay attached; new payouts are closed.</div>
          ) : null}
          <nav className="dashboard-section-nav" aria-label="Affiliate sections">
            {(['overview', 'activity', 'settings'] as const).map(item => <button type="button" key={item}
              aria-current={section === item ? 'page' : undefined} onClick={() => setSection(item)}>
              {{ overview: 'Overview', activity: 'Activity', settings: 'Settings' }[item]}
            </button>)}
          </nav>
          <div className="affiliate-facts">
            <article>
              <span>Available</span>
              <strong className={available < 0 ? 'is-negative' : undefined}>{money(available, program.balance.currency)}</strong>
              <small>{available < 0 ? `${openReversals} open chargeback${openReversals === 1 ? '' : 's'}` : 'Ready to request'}</small>
            </article>
            <article><span>Reserved</span><strong>{money(program.balance.reserved_cents, program.balance.currency)}</strong><small>Payout in review</small></article>
            <article><span>Paid out</span><strong>{money(program.balance.paid_cents, program.balance.currency)}</strong><small>Already sent</small></article>
            <article><span>Referred customers</span><strong>{program.referred.referred}</strong><small>{program.referred.paying} have paid</small></article>
          </div>

          <section className="affiliate-link-card" hidden={section !== 'overview'}>
            <div>
              <span>Your referral link</span>
              <strong>{program.url}</strong>
              <small>Share this link to connect new customers to your affiliate account.</small>
            </div>
            <button className="button button--dash-quiet" onClick={() => void copyLink()}>
              {copied ? <Check /> : <Copy />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </section>

          <section className="affiliate-agreement" hidden={section !== 'overview'} aria-labelledby="affiliate-agreement-title">
            <header><h2 id="affiliate-agreement-title">Your agreement</h2><p>The rates and durations approved for your account.</p></header>
            <dl>
              <div><dt>Customer discount</dt><dd>{program.terms.discount_percent}% <span>{durationLabel(program.terms.discount_duration_days)}</span></dd></div>
              <div><dt>Your commission</dt><dd>{program.terms.commission_percent}% <span>{durationLabel(program.terms.commission_duration_days)}</span></dd></div>
              {program.terms.lifetime_plan && !['free', 'locked'].includes(program.terms.lifetime_plan) && <div><dt>Included plan</dt><dd>{program.terms.lifetime_plan.replace(/_/g, ' ')} <span>Lifetime, no subscriber requirement</span></dd></div>}
            </dl>
            <p>Commission is calculated on eligible paid invoices, excluding tax.</p>
            {(program.terms.discount_duration_days !== null || program.terms.commission_duration_days !== null) && <p>
              Time-limited terms run from approval on {new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(program.terms.approved_at))}.
            </p>}
          </section>
          <div className="affiliate-grid" hidden={section !== 'settings'}>
            <form className="people-composer affiliate-id-form" onSubmit={submitSlug}>
              <div className="people-composer__head">
                <h2>Referral ID</h2>
                <p>Used in bckt.io/r/{program.slug}. Changing it keeps previous IDs as aliases.</p>
              </div>
              <div className="affiliate-id-row">
                <label className="people-email">
                  <span>New ID</span>
                  <input name="slug" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" title="Use lowercase letters, numbers, and single hyphens between words." defaultValue={program.slug} minLength={3} maxLength={32} required />
                </label>
                <button className="button button--dash-quiet" disabled={saveSlug.isPending} data-state={saveSlug.isPending ? 'loading' : undefined}>
                  {saveSlug.isPending ? 'Saving…' : 'Save ID'}
                </button>
              </div>
              {program.aliases.length > 1 ? (
                <p className="affiliate-aliases">
                  Also live: {program.aliases.filter((item) => !item.is_current).map((item) => item.slug).join(', ')}
                </p>
              ) : null}
            </form>

            <form className="people-composer" onSubmit={submitPayout}>
              <div className="people-composer__head">
                <h2>Payout method</h2>
                <p>We pay the accumulated balance. VAT is not withheld. You declare the income if required.</p>
              </div>
              <div className="people-presets">
                <div role="radiogroup" aria-label="Payout method">
                  <button type="button" role="radio" aria-checked={method === 'paypal'} className={method === 'paypal' ? 'is-on' : undefined} onClick={() => setMethod('paypal')}>
                    <Wallet /><strong>PayPal</strong><span>Legal name and PayPal email.</span>
                  </button>
                  <button type="button" role="radio" aria-checked={method === 'bank'} className={method === 'bank' ? 'is-on' : undefined} onClick={() => setMethod('bank')}>
                    <Landmark /><strong>Bank</strong><span>IBAN. SWIFT optional.</span>
                  </button>
                </div>
              </div>
              <label className="people-email">
                <span>Country</span>
                <select name="country" defaultValue={program.payout.country ?? 'NL'} required>
                  {query.data?.countries.map((country) => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
              </label>
              <label className="people-email">
                <span>Full legal name</span>
                <input name="full_name" maxLength={120} defaultValue={program.payout.full_name ?? ''} minLength={3} required />
              </label>
              {method === 'paypal' ? (
                <label className="people-email">
                  <span>PayPal email</span>
                  <input maxLength={320} name="paypal_email" type="email" defaultValue={program.payout.paypal_email ?? ''} required />
                </label>
              ) : (
                <>
                  <label className="people-email">
                    <span>IBAN</span>
                    <input name="iban" minLength={15} maxLength={42} pattern="[A-Za-z]{2}[0-9]{2}[A-Za-z0-9 ]{11,38}" title="Enter an IBAN starting with its two-letter country code." defaultValue="" placeholder="NL00BANK0123456789" required />
                    {program.payout.bank_iban ? <small>On file: {program.payout.bank_iban}</small> : null}
                  </label>
                  <label className="people-email">
                    <span>SWIFT / BIC, optional</span>
                    <input name="swift" maxLength={11} pattern="[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?" title="Use an 8 or 11 character SWIFT/BIC code." defaultValue={program.payout.bank_swift ?? ''} />
                  </label>
                </>
              )}
              <div className="people-composer__actions">
                <button className="button button--dash-quiet" disabled={saveMethod.isPending} data-state={saveMethod.isPending ? 'loading' : undefined}>
                  {saveMethod.isPending ? 'Saving…' : 'Save payout details'}
                </button>
              </div>
            </form>
          </div>

          <section className="people-block" hidden={section !== 'overview'}>
            <div className="people-block__head">
              <h2>Withdraw</h2>
              <p>Minimum {formatMoney(query.data?.min_payout_cents ?? 500)}. Open chargebacks must be cleared first.</p>
            </div>
            {!program.payout.method && <p className="affiliate-payout-hint">Add a payout method to withdraw your earnings.{' '}
              <button type="button" className="button button--dash-quiet" onClick={() => setSection('settings')}>Set up payouts</button>
            </p>}
            {available < 0 ? (
              <p className="affiliate-debt">
                Balance is {money(available, program.balance.currency)} because {openReversals || 'one or more'} buyer
                {openReversals === 1 ? ' has' : 's have'} reversed. Later commissions repay this before cash leaves.
                If Stripe wins the dispute, the amount is added back.
              </p>
            ) : null}
            <button
              className="button button--dash-primary"
              disabled={!canWithdraw || requestPayout.isPending || program.status !== 'active'}
              onClick={() => requestPayout.mutate()}
            >
              {requestPayout.isPending ? 'Requesting…' : `Request ${money(Math.max(0, available), program.balance.currency)}`}
            </button>
          </section>

          <section className="people-block" hidden={section !== 'activity'}>
            <div className="people-block__head">
              <h2>Commission activity</h2>
              <p>Paid invoices add. Refunds and chargebacks subtract. Restores appear if Stripe wins the dispute.</p>
            </div>
            {program.commissions.length ? (
              <div className="affiliate-ledger">
                {program.commissions.map((row) => (
                  <article key={row.id} className={row.commission_cents < 0 ? 'is-negative' : undefined}>
                    <div>
                      <strong>{kindLabel(row.kind, row.note)}</strong>
                      <time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(row.created_at))}</time>
                    </div>
                    <span>{money(row.invoice_exclusive_cents, row.currency)} excl. tax</span>
                    <b>{row.commission_cents > 0 ? '+' : ''}{money(row.commission_cents, row.currency)}</b>
                  </article>
                ))}
              </div>
            ) : (
              <p className="affiliate-empty">No commissions yet. They appear here when a referred account pays.</p>
            )}
          </section>

          <section className="people-block" hidden={section !== 'activity'}>
            <div className="people-block__head">
              <h2>Payout history</h2>
            </div>
            {program.payouts.length ? (
              <div className="affiliate-ledger">
                {program.payouts.map((row) => (
                  <article key={row.id}>
                    <div>
                      <strong>{row.method === 'paypal' ? 'PayPal' : 'Bank transfer'}</strong>
                      <time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(row.requested_at))}</time>
                    </div>
                    <span className={`affiliate-status affiliate-status--${row.status}`}>{row.status}</span>
                    <b>{money(row.amount_cents, row.currency)}</b>
                  </article>
                ))}
              </div>
            ) : (
              <p className="affiliate-empty">No payouts yet. Your completed and pending payout requests will appear here.</p>
            )}
          </section>

          <section className="people-block" hidden={section !== 'overview'}>
            <div className="people-block__head">
              <h2>Creator FAQ</h2>
            </div>
            <Faq items={creatorFaqs} />
          </section>
        </>
      )}
      
      <ModalPresence>{applyModalOpen && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button type="button" className="secret-modal__scrim" onClick={() => setApplyModalOpen(false)} />
          <form
            className="purge-dialog"
            onSubmit={(e) => {
              e.preventDefault();
              submitApplication.mutate({
                display_name: applyName.trim(),
                requested_slug: applySlug.trim().toLowerCase(),
                platform_url: applyPlatform.trim(),
                promotion_plan: applyPlan.trim(),
                contact_info: applyContact.trim(),
              });
            }}
          >
            <span>
              <Wallet />
            </span>
            <h2>Apply for Creator Partner</h2>
            <p>
              Standard terms: 10% lifetime commission and 10% off the first month for customers. Large organizations can negotiate rates and durations at hello@bckt.io.
            </p>
            {submitApplication.error && (
              <div className="settings-notice settings-notice--error" style={{ margin: 'var(--space-md) 0' }}>
                {(submitApplication.error as Error).message || 'Could not submit application.'}
              </div>
            )}

            <div className="purge-options">
              <label>
                Display name / Brand
                <input
                  required
                  type="text"
                  placeholder="e.g. Thomas or Server Hosting Co."
                  minLength={2} maxLength={100} value={applyName}
                  onChange={(e) => {
                    setApplyName(e.target.value);
                    if (!applySlug) {
                      setApplySlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30));
                    }
                  }}
                />
              </label>

              <label>
                Preferred referral code
                <input
                  required
                  type="text"
                  pattern="[a-z0-9][a-z0-9\-]{1,30}[a-z0-9]"
                  placeholder="e.g. thomas or myserver"
                  minLength={3} maxLength={32} value={applySlug}
                  onChange={(e) => setApplySlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                />
                <small style={{ color: 'var(--color-muted)', fontSize: '11px', marginTop: '2px' }}>
                  Your public vanity link: <strong>bckt.io/r/{applySlug || 'yourcode'}</strong>
                </small>
              </label>

              <label>
                Primary channel / Community URL
                <input
                  required
                  type="text"
                  placeholder="e.g. youtube.com/@channel, discord.gg/server, github.com/user"
                  minLength={3} maxLength={300} value={applyPlatform}
                  onChange={(e) => setApplyPlatform(e.target.value)}
                />
              </label>

              <label>
                Audience & promotion plan
                <textarea
                  required
                  rows={3}
                  style={{
                    padding: 'var(--space-sm) var(--space-md)',
                    border: 'var(--rule-hair) solid var(--color-rule)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-paper-3, #151614)',
                    color: 'var(--color-ink)',
                    fontSize: 'var(--text-sm)',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                  placeholder="Tell us about your audience, community, or how you plan to share and integrate BCKT."
                  minLength={10} maxLength={2000} value={applyPlan}
                  onChange={(e) => setApplyPlan(e.target.value)}
                />
              </label>

              <label>
                Contact email or Discord handle (optional)
                <input
                  type="text"
                  placeholder="e.g. Discord username"
                  maxLength={200} value={applyContact}
                  onChange={(e) => setApplyContact(e.target.value)}
                />
              </label>
            </div>

            <div className="dialog-actions">
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setApplyModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--dash-primary"
                disabled={submitApplication.isPending}
              >
                {submitApplication.isPending ? 'Submitting...' : 'Submit application'}
              </button>
            </div>
          </form>
        </div>
      )}</ModalPresence>

      <p className="affiliate-footnote">
        <Link2 /> Questions about your partnership? <a href="mailto:hello@bckt.io">hello@bckt.io</a>
      </p>
    </div>
  );
}
